import React, { useState, useRef ,useEffect, useCallback} from 'react';
import { ReactTabulator } from 'react-tabulator';
import DatePicker from "react-datepicker";
import {Card} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import "react-datepicker/dist/react-datepicker.css";
import "tabulator-tables/dist/css/tabulator_semanticui.min.css"; 
import { jwtDecode } from 'jwt-decode';
import loader from '../../assets/img/loading.gif'
import { API_BASE_URL } from '../../config/app-axios.jsx';
import { clearUserState } from '../../reducers/userReducers.jsx';
import { clearMenuState } from '../../reducers/menuReducers.jsx';
import { clearLangCodeState } from '../../reducers/langCodeReducers.jsx';

// Tabulator columns 목록
const initializeColumns= (t) => {
	return [
		{formatter:"rowSelection", titleFormatter:"rowSelection", headerHozAlign: 'center', hozAlign:"center", headerSort:false, width:30, cellClick:function(e, cell){
			cell.getRow().toggleSelect();
		}},
		{ title: t('APPROVAL.요청일', { defaultValue: '요청일' }), field: 'created_date', headerHozAlign: 'center', hozAlign: 'center', width:180, formatter:function(cell, formatterParams, onRendered){
			var value = cell.getValue();
			value = moment(value).format("DD/MM/YYYY HH:mm:ss");
			return value;
		} },
		{ title: t('APPROVAL.결재명', { defaultValue: '결재명' }), field: 'approval_type', headerHozAlign: 'center', hozAlign: 'center', width:160 },
		{ title: t('APPROVAL.대출접수번호', { defaultValue: '대출접수번호' }), field: 'loan_application_id', headerHozAlign: 'center', hozAlign: 'center', width: 200 },
		{ title: t('APPROVAL.대출자', { defaultValue: '대출자' }), field: 'name', headerHozAlign: 'center', hozAlign: 'center', width:160, formatter: function (cell, formatterParams, onRendered) {
			cell.getElement().style.fontWeight = 'bold';
			return cell.getValue();
		} },
		{ title: t('APPROVAL.대출금액', { defaultValue: '대출금액' }), field: 'loan_amount', headerHozAlign: 'center', hozAlign: 'right', width:150, formatter:"money", formatterParams:{ decimal:",", thousand:".", negativeSign:true, precision:false }, bottomCalc: "sum", bottomCalcFormatter: "money", bottomCalcFormatterParams: { decimal:",", thousand:".", negativeSign:true, precision:false } },
		{ title: t('APPROVAL.결재상태', { defaultValue: '결재상태' }), field: 'approval_status', headerHozAlign: 'center', hozAlign: 'center', width:160 },
		{ title: t('APPROVAL.대출상태', { defaultValue: '대출상태' }), field: 'executiontype', headerHozAlign: 'center', hozAlign: 'center', width:160 },
	];
}
// 나의 결재 요청 화면 
const Sanc001 = () => {
	const { t,i18n }          			  = useTranslation();
	const dispatch                        = useDispatch();
	const oneMonthAgo         			  = new Date();
	const tableRef            			  = useRef(null);

	// 초기 값
	const [loading, setLoading]           = useState(false);
	const [startDate, setStartDate]       = useState(oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1));
	const [endDate, setEndDate]           = useState(new Date());
	const [rowsPerPage, setRowsPerPage]   = useState(20);
	const [filterField, setFilterField]   = useState('approval_type');
	const [filterType, setFilterType]     = useState('like');
	const [filterValue, setFilterValue]   = useState('');
	const [columns, setColumns]           = useState(initializeColumns(t));
	const [totalCount, setTotalCount]     = useState(0);

	// remote 방식에 따른 페이징 처리
	const apiUrl   = API_BASE_URL;
	const userInfo = useSelector(state => state.login.userInfo);
	const token    = userInfo?.access_token;

	// ①. 강제로 쿠키 삭제를 통한 로그인 정보가 없는 경우
    const persistInfo = sessionStorage.getItem('persist:login');

    if (!persistInfo) {
        window.location.href = '/login';
    };

	// ②. 토큰 만료 체크 루틴
	if (token) {
		const decodedToken            = jwtDecode(token);
		const expirationTimeInSeconds = decodedToken.exp;
		const currentTimeInSeconds    = Math.floor(Date.now() / 1000);

		if (expirationTimeInSeconds < currentTimeInSeconds) {
			// 토큰이 만료되었을 경우 로그인 페이지로 리디렉션
			dispatch(clearUserState());
			dispatch(clearMenuState());
			dispatch(clearLangCodeState());
			localStorage.removeItem('langCodeInfo');
			window.location.href = '/login';
		};
	};

	// 시작일 값 변경
	const handleChangeStartDate = (date) => {
		setStartDate(date);
	};

	// 종료일 값 변경
	const handleChangeEndDate = (date) => {
		setEndDate(date);
	};

	// i18 선택된 언어에 따라 필요한 필드 값 번역
	const translateItem = useCallback((item, language) => ({
		...item,
		executiontype   : item.executiontype ? t(`CODE_INFORMATION.${item.executiontype}`, { lng: language }) : '',
		approval_type   : item.approval_type ? t(`APPROVAL_TYPE.${item.approval_type}`, { lng: language }) : '',
		approval_status : item.approval_status ? t(`APPROVAL_STATUS.${item.approval_status}`, { lng: language }) : '',
	}), [t]);

	// Tabulator Options
	// options 객체를 상태로 관리
	const [options, setOptions] = useState({
		ajaxURL     : `${apiUrl}/sanc/sanc_001`,
		ajaxConfig  : {
			method  : 'GET',
			headers : {
				'Content-Type' : 'application/json',
				'Authorization': `Bearer ${token}`,
			},
		},
		ajaxParams : {
			startDate : moment(startDate).format('YYYY-MM-DD'),
			endDate   : moment(endDate).format('YYYY-MM-DD'),
			page      : tableRef.current && tableRef.current.table ? tableRef.current.getPage() : 1,
			size      : tableRef.current && tableRef.current.table ? tableRef.current.getPageSize() : 20, // 페이지 크기 정보
		},
		layout                 : "fitColumns",
		pagination             : true,
		paginationMode         : "remote",
		paginationSize         : rowsPerPage,
		paginationSizeSelector : [20, 40, 60, 80, 100],
		ajaxResponse : function (url, params, response) {

			const total_count    = response.total_count || 0;
			const translatedData = response.data.map(item => translateItem(item, i18n.language));
			const responseData   = {
				last_page : response.last_page,
				data      : translatedData,
			};

			setTotalCount(total_count);

			return responseData;
		}
	});

	// 로딩 중일 때 이전의 pagination 관련 정보를 유지하도록 설정
	const loadingOptions = {
		pagination             : true,
		paginationMode         : "remote",
		paginationSize         : rowsPerPage, // 이전 페이지 사이즈를 유지
		paginationSizeSelector : [20, 40, 60, 80, 100],
	};

	// 조회 버튼 작동 시 실행되는 함수
	const handleQuery = useCallback(
		async () => {
			setLoading(true);

			// ReactTabulator의 내부 로직을 사용하여 API 호출
			try {
				// ReactTabulator에서 사용하는 API URL과 Params를 사용하여 호출
				const url  = new URL(options.ajaxURL);
				url.search = new URLSearchParams({
					...options.ajaxParams,
					page : tableRef.current?.getPage(),
					size : tableRef.current?.getPageSize(),
				});

				const data = await fetch(url, {
					method  : options.ajaxConfig.method,
					headers : options.ajaxConfig.headers,
				});

				const responseData = await data.json();

				// 전달 받은 총 데이터 건수
				const total_count = responseData.total_count || 0;
		
				// 번역이 필요한 부분
				const translatedData = responseData.data.map(item => translateItem(item, i18n.language));
		
				// 페이징 및 데이터 리턴
				const updatedData = {
					last_page : responseData.last_page, // 전체 페이지 수 계산
					data      : translatedData, // 실제 테이블 데이터
				};

				// 총 건수를 화면에 표현하기 위해서
				setTotalCount(total_count);

				// 테이블 데이터를 초기화하고 새로운 데이터로 업데이트
				if (tableRef.current) {
					// 데이터 로드가 완료된 후에만 테이블 데이터를 변경하도록 함
					if (!loading) {
						tableRef.current.clearData();
						tableRef.current.setData(updatedData.data || []);
					}
				}

				setLoading(false);

			} catch (error) {
				console.error('Error in fetchSanc001Data : ', error);
				setLoading(false);
			} 
		}, [options, i18n.language, translateItem, tableRef, loading]
	);

	// Filter 값에 따른 Tabulator 변화가 일어나는 함수
	const updateFilter = () => {
		if (tableRef.current) {
			tableRef.current.setFilter(filterField, filterType, filterValue);
		}
	};

	// value값을 입력할 때마다 해당되는 Tabulator가 화면에 보이도록 함수호출하는 부분
	const handleKeyUp = () => {
		updateFilter();
	};

	// 조회 조건이 바뀜에 따라 ajaxParams 동적 변경
	useEffect(() => {
		setOptions((prevOptions) => {
			const updatedOptions = {
				...prevOptions,
				ajaxParams: {
					...prevOptions.ajaxParams,
					startDate               : moment(startDate).format('YYYY-MM-DD'),
					endDate                 : moment(endDate).format('YYYY-MM-DD'),
				},
			};
			return updatedOptions;
		});
	}, [startDate, endDate]);

	// i18 언어에 맞춰서 자동 갱신 (컬럼명, Grid 내용)
	useEffect(() => {
		setColumns(initializeColumns(t));
	}, [i18n.language, t, options, handleQuery]);

	// UI
	return (
		<Card style={{ height: '100%' }}>
			<Card.Header><strong>{t('APPROVAL.나의 결재 요청', { defaultValue: '나의 결재 요청' })}</strong></Card.Header>
			<Card.Body className='responsive-card-body'>
				<div className="panel-body">
					{/* <form action="/" method="POST"> */}
						<div className="row ms-0">
							<div className="form-group col-lg-3 col-md-12 col-xs-12 mb-2 ps-0 rounded-2">
								<label className="form-label" htmlFor="date_id">
									<i className="bi bi-calendar3"></i> {t('APPROVAL.요청일', { defaultValue: '요청일' })}
								</label>

								<div className="input-group input-daterange">
									<div className='col'>
										<DatePicker
											className='form-control'
											selected={startDate}
											onChange={handleChangeStartDate}
											dateFormat="dd/MM/yyyy"
										/>
									</div>

									<div className="col-auto">
										<span className="input-group-text input-group-addon"> ~ </span>
									</div>

									<div className='col'>
										<DatePicker
											className='form-control'
											selected={endDate}
											onChange={handleChangeEndDate}
											dateFormat="dd/MM/yyyy"
										/>
									</div>
								</div>
							</div>

							<div className="form-group col-lg-4 col-md-12 col-xs-12"></div>
							<div className="form-group col-lg-4 col-md-12 col-xs-12"></div>

							<div className="form-group col-lg-1 col-md-12 col-xs-12 ps-0 rounded-2">
								<div className="mb-3"></div>
								<button type="button" className="btn btn-success float-end float-bottom w-100 h-30.5px align-baseline mt-8px" onClick={handleQuery}>{t('LOAN_APPLICATION.조회', { defaultValue: '조회' })}</button>
							</div>


							<div className="mt-3 mb-3 ps-0">
								<i id="filter-icon" className="cursor-pointer fa fa-plus-square" data-bs-toggle="collapse" href="#collapseExample" aria-expanded="false" aria-controls="collapseExample"></i>
								<span> Filter</span>
							</div>

							<div className="collapse show" id="collapseExample">
								<div className="row">
									<div className="form-group col-lg-2 col-md-12 col-xs-12 ps-0 rounded-2 mb-1">
										<select 
											id="filter-field" 
											className="form-select col-sm-3" 
											value={filterField}
											onChange={(e) => setFilterField(e.target.value)}
										>
											<option value="loan_status">{t('APPROVAL.결재명', { defaultValue: '결재명' })}</option>
											<option value="loan_application_id">{t('APPROVAL.대출접수번호', { defaultValue: '대출접수번호' })}</option>
											<option value="name">{t('APPROVAL.대출자', { defaultValue: '대출자' })}</option>
										</select>
									</div>
						
									<div className="form-group col-lg-2 col-md-12 col-xs-12 ps-0 rounded-2 mb-1">
										<select 
											id="filter-type" 
											className="form-select col-sm-3"
											value={filterType}
											onChange={(e) => setFilterType(e.target.value)}
										>
											<option value="like">LIKE</option>
											<option value="=">=</option>
											<option value="<">&lt;</option>
											<option value="<=">&le;</option>
											<option value=">">&gt;</option>
											<option value=">=">&ge;</option>
											<option value="!=">!=</option>
										</select>
									</div>
						
									<div className="form-group col-lg-8 col-md-12 col-xs-12 ps-0 mb-3 rounded-2">
										<input 
											id="filter-value" 
											type="text" 
											className="form-control col-sm-6" 
											name="filter-value" 
											placeholder={t('COMMON.검색할 내용을 입력하세요', { defaultValue: '검색할 내용을 입력하세요' })}
											value={filterValue}
											onChange={(e) => setFilterValue(e.target.value)}
											onKeyUp={handleKeyUp}
										/>
									</div>
								</div>
							</div>
						</div>
					{/* </form> */}
				</div>

				{/* 로딩 관련 gif */}
				{loading && 
					<div
						style={{
							position: 'fixed',
							top: '50%',
							left: '55%',
							transform: 'translate(-50%, -50%)',
							zIndex: 9999,
						}}
					>
						<img src={loader} alt="Loading..." />
					</div>
				}

				{/* Tabulator */}
				<div id="tabulator-total-info" className='d-flex justify-content-end fw-bold'>{t(`LOAN_APPLICATION.총 {{count}}건`, { count: totalCount, defaultValue: '총 {{count}}건' })}</div>
				<ReactTabulator
					key         = {i18n.language + JSON.stringify(columns) + JSON.stringify(options)} // 언어 변경 및 옵션 변경에 따라 컴포넌트 리렌더링
					onRef       = {(r) => (tableRef.current = r.current)}
					columns     = {columns}
					options     = {loading ? loadingOptions : options} // 로딩 중일 때는 이전의 pagination 관련 정보를 유지하고 데이터를 변경하지 않도록 함
					placeholder = "No Data Found"
					className   = "responsive-tabulator"
				/>
			</Card.Body>
			<div className="card-footer d-flex justify-content-start mt-auto">
				{/* <button type="button" className="btn btn-outline-info mb-1 me-1 w-100px">버튼1</button>
				<button type="button" className="btn btn-outline-success me-1 mb-1 w-100px">버튼2</button> */}
			</div>
			
		</ Card>
	);
};

export default Sanc001;